import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as hrStaffSepPaymentAPI from '../../api/HRAPI/hrStaffSepPaymentAPI';

// Async Thunks
// =============================================

export const fetchConsolidateNoForHRSep = createAsyncThunk(
    'hrStaffSepPayment/fetchConsolidateNoForHRSep',
    async (params, { rejectWithValue }) => {
        try {
            const response = await hrStaffSepPaymentAPI.getConsolidateNoForHRSep(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Consolidate Numbers');
        }
    }
);

export const fetchCostcenterPaidAmount = createAsyncThunk(
    'hrStaffSepPayment/fetchCostcenterPaidAmount',
    async (params, { rejectWithValue }) => {
        try {
            const response = await hrStaffSepPaymentAPI.getCostcenterPaidAmount(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Cost Center Paid Amount');
        }
    }
);

export const fetchCompanyBankDetails = createAsyncThunk(
    'hrStaffSepPayment/fetchCompanyBankDetails',
    async (_, { rejectWithValue }) => {
        try {
            const response = await hrStaffSepPaymentAPI.getCompanyBankDetails();
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Bank Details');
        }
    }
);

export const saveHRSEPPayment = createAsyncThunk(
    'hrStaffSepPayment/saveHRSEPPayment',
    async (params, { rejectWithValue }) => {
        try {
            const response = await hrStaffSepPaymentAPI.saveHRSEPPayment(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to save SEP Payment');
        }
    }
);

// Initial State
// =============
const initialState = {
    consolidateNumbers: [],
    paidData: null, // { PaymentExistStatus, PaidDataList, ... }
    bankList: [],
    saveResult: null,
    saveStatus: null, // null | 'pending' | 'success' | 'failed'

    loading: {
        consolidateNumbers: false,
        paidData: false,
        bankList: false,
        save: false,
    },

    errors: {
        consolidateNumbers: null,
        paidData: null,
        bankList: null,
        save: null,
    },
};

const hrStaffSepPaymentSlice = createSlice({
    name: 'hrStaffSepPayment',
    initialState,
    reducers: {
        clearConsolidateNumbers: (state) => {
            state.consolidateNumbers = [];
            state.errors.consolidateNumbers = null;
        },
        clearPaidData: (state) => {
            state.paidData = null;
            state.errors.paidData = null;
        },
        clearSaveResult: (state) => {
            state.saveResult = null;
            state.saveStatus = null;
            state.errors.save = null;
        },
        resetHRStaffSepPayment: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchConsolidateNoForHRSep.pending, (state) => {
                state.loading.consolidateNumbers = true;
                state.errors.consolidateNumbers = null;
            })
            .addCase(fetchConsolidateNoForHRSep.fulfilled, (state, action) => {
                state.loading.consolidateNumbers = false;
                state.consolidateNumbers = action.payload?.Data || [];
            })
            .addCase(fetchConsolidateNoForHRSep.rejected, (state, action) => {
                state.loading.consolidateNumbers = false;
                state.errors.consolidateNumbers = action.payload;
                state.consolidateNumbers = [];
            });

        builder
            .addCase(fetchCostcenterPaidAmount.pending, (state) => {
                state.loading.paidData = true;
                state.errors.paidData = null;
            })
            .addCase(fetchCostcenterPaidAmount.fulfilled, (state, action) => {
                state.loading.paidData = false;
                state.paidData = action.payload?.Data || null;
            })
            .addCase(fetchCostcenterPaidAmount.rejected, (state, action) => {
                state.loading.paidData = false;
                state.errors.paidData = action.payload;
                state.paidData = null;
            });

        builder
            .addCase(fetchCompanyBankDetails.pending, (state) => {
                state.loading.bankList = true;
                state.errors.bankList = null;
            })
            .addCase(fetchCompanyBankDetails.fulfilled, (state, action) => {
                state.loading.bankList = false;
                state.bankList = action.payload?.Data || [];
            })
            .addCase(fetchCompanyBankDetails.rejected, (state, action) => {
                state.loading.bankList = false;
                state.errors.bankList = action.payload;
                state.bankList = [];
            });

        builder
            .addCase(saveHRSEPPayment.pending, (state) => {
                state.loading.save = true;
                state.errors.save = null;
                state.saveStatus = 'pending';
            })
            .addCase(saveHRSEPPayment.fulfilled, (state, action) => {
                state.loading.save = false;
                state.saveResult = action.payload;

                const responseStr = typeof action.payload?.Data === 'string'
                    ? action.payload.Data
                    : (typeof action.payload === 'string' ? action.payload : '');

                const successMessage = responseStr.split('$')[0];

                if (successMessage === 'Submited') {
                    state.saveStatus = 'success';
                } else {
                    state.saveStatus = 'failed';
                    state.errors.save = responseStr || 'Save failed';
                }
            })
            .addCase(saveHRSEPPayment.rejected, (state, action) => {
                state.loading.save = false;
                state.errors.save = action.payload;
                state.saveStatus = 'failed';
                state.saveResult = null;
            });
    },
});

export const {
    clearConsolidateNumbers,
    clearPaidData,
    clearSaveResult,
    resetHRStaffSepPayment,
} = hrStaffSepPaymentSlice.actions;

// Selectors
// =========
export const selectConsolidateNumbersArray = (state) => {
    const nos = state.hrStaffSepPayment.consolidateNumbers;
    return Array.isArray(nos) ? nos : [];
};
export const selectConsolidateNumbersLoading = (state) => state.hrStaffSepPayment.loading.consolidateNumbers;

export const selectPaidData = (state) => state.hrStaffSepPayment.paidData;
export const selectPaidDataLoading = (state) => state.hrStaffSepPayment.loading.paidData;

export const selectBankListArray = (state) => {
    const banks = state.hrStaffSepPayment.bankList;
    return Array.isArray(banks) ? banks : [];
};
export const selectBankListLoading = (state) => state.hrStaffSepPayment.loading.bankList;

export const selectSaveStatus = (state) => state.hrStaffSepPayment.saveStatus;
export const selectSaveLoading = (state) => state.hrStaffSepPayment.loading.save;
export const selectSaveError = (state) => state.hrStaffSepPayment.errors.save;

export default hrStaffSepPaymentSlice.reducer;
